/**
 * PurchaseOrderDetailPage — iter172
 *
 * One Purchase Order, fully drilled-in:
 *   • Header card with PO #, supplier, dates, totals, status flags.
 *   • Ordered lines table from Purchase_Order_Details (PO-scoped via
 *     PO_Heading).
 *   • "Invoices received" section listing every Vendor_Shipments row
 *     where POID matches this PO. Clicking a row opens the shared
 *     `VendorShipmentDetailModal` (from VendorShipmentsPage) for the
 *     full invoice header / attachments / journal entries.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowUUpLeft, Truck, Receipt, ShoppingBag } from "@phosphor-icons/react";

import HeaderBrand from "@/components/HeaderBrand";
import { Button } from "@/components/ui/button";
import {
  fetchPurchaseOrder,
  fetchPurchaseOrderVendorShipments,
} from "@/lib/api";
import { VendorShipmentDetailModal } from "@/pages/VendorShipmentsPage";


function fmtMoney(v, currency = "USD") {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
}


export default function PurchaseOrderDetailPage({ user }) {
  const navigate = useNavigate();
  const { poId } = useParams();

  const [head, setHead] = useState(null);
  const [lines, setLines] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    if (!user?.email) navigate("/", { replace: true });
  }, [user?.email, navigate]);

  useEffect(() => {
    let alive = true;
    setBusy(true); setError("");
    Promise.all([
      fetchPurchaseOrder(poId),
      fetchPurchaseOrderVendorShipments(poId).catch(() => ({ items: [] })),
    ])
      .then(([detail, inv]) => {
        if (!alive) return;
        setHead(detail.head);
        setLines(detail.lines || []);
        setInvoices(inv.items || []);
      })
      .catch((e) => {
        if (!alive) return;
        const detail = e?.response?.data?.detail || e?.message || "Failed to load PO";
        setError(detail);
      })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [poId]);

  const totals = useMemo(() => {
    const subtotal = head?.subtotal || 0;
    const tax = (head?.hst || 0) + (head?.tax || 0);
    const shipping = head?.shipment_cost || 0;
    return { subtotal, tax, shipping, grand: subtotal + tax + shipping };
  }, [head]);

  const invoicesTotal = useMemo(
    () => invoices.reduce((acc, inv) => acc + Number(inv.total || 0), 0),
    [invoices]
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="po-detail-page">
      <HeaderBrand user={user} />
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="po-detail-back"
                     onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/admin/suppliers"))}
                     className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 py-1 rounded-sm hover:bg-slate-100">
              <ArrowUUpLeft size={14} weight="bold" /> Back
            </button>
            <span className="text-slate-300">/</span>
            <Truck size={20} weight="bold" className="text-indigo-700" />
            <h1 className="text-xl font-bold font-['Chivo'] text-slate-900">
              Purchase order <span className="font-mono text-indigo-700" data-testid="po-detail-poid">{head?.poid || poId}</span>
            </h1>
          </div>
          {head?.supplier && (
            <button
              data-testid="po-detail-supplier-link"
              onClick={() => navigate(`/admin/suppliers?supplier=${encodeURIComponent(head.supplier)}`)}
              className="text-xs font-semibold text-slate-600 hover:text-indigo-700 hover:underline"
            >
              {head.supplier} →
            </button>
          )}
        </div>

        {busy && (
          <div className="text-sm text-slate-400 py-6 text-center" data-testid="po-detail-loading">
            Loading purchase order…
          </div>
        )}
        {error && (
          <div className="text-xs px-3 py-2 rounded-sm border bg-red-50 border-red-200 text-red-800"
                data-testid="po-detail-error">
            {error}
          </div>
        )}

        {head && !busy && (
          <>
            {/* Header KPIs */}
            <section className="bg-white border border-slate-200 rounded-sm p-3" data-testid="po-detail-header-card">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <KPI label="PO date" value={head.po_date || "—"} />
                <KPI label="Buyer"    value={head.buyer_customer || "—"} />
                <KPI label="Status"   value={head.last_action || "—"} />
                <KPI label="Shipment" value={head.shipment_type || "—"} />
                <KPI label="Delivery" value={head.delivery_time || "—"} />
                <KPI label="Subtotal"      value={fmtMoney(totals.subtotal)} mono />
                <KPI label="HST + Tax"     value={fmtMoney(totals.tax)}      mono />
                <KPI label="Shipping cost" value={fmtMoney(totals.shipping)} mono />
                <KPI label="Grand total"   value={fmtMoney(totals.grand)}    mono highlight />
                <KPI label="Line count"    value={String(head.line_count || lines.length || 0)} mono />
              </div>
            </section>

            {/* Receipt flow chips */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="po-detail-flow-chips">
              <FlowChip label="Ordered"   value={head.qty_ordered}    accent="slate" />
              <FlowChip label="At customs" value={head.qty_at_customs} accent="amber" />
              <FlowChip label="In transit" value={head.qty_in_transit} accent="indigo" />
              <FlowChip label="Arrived"    value={head.qty_arrived}    accent="emerald" />
            </section>

            {/* Lines */}
            <section className="bg-white border border-slate-200 rounded-sm" data-testid="po-detail-lines-section">
              <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
                <ShoppingBag size={14} weight="bold" className="text-slate-600" />
                <span className="text-sm font-bold text-slate-800">Ordered lines</span>
                <span className="text-[11px] text-slate-400 font-mono">({lines.length})</span>
              </div>
              {lines.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-4"
                      data-testid="po-detail-lines-empty">
                  No Purchase_Order_Details rows for this PO.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="po-detail-lines-table">
                    <thead className="bg-slate-50 text-left text-slate-600">
                      <tr>
                        <th className="px-2 py-1.5 font-semibold">#</th>
                        <th className="px-2 py-1.5 font-semibold">Product</th>
                        <th className="px-2 py-1.5 font-semibold">Status</th>
                        <th className="px-2 py-1.5 font-semibold">Invoice #</th>
                        <th className="px-2 py-1.5 font-semibold text-right">Qty</th>
                        <th className="px-2 py-1.5 font-semibold text-right">Counted</th>
                        <th className="px-2 py-1.5 font-semibold text-right">Unit price</th>
                        <th className="px-2 py-1.5 font-semibold text-right">Line total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((ln) => (
                        <tr key={ln.POdetailID || ln.id}
                             data-testid={`po-line-${ln.POdetailID || ln.id}`}
                             className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-mono text-slate-500">{ln.POdetailID || ln.id}</td>
                          <td className="px-2 py-1.5 font-mono">{ln.Product_Name || "—"}</td>
                          <td className="px-2 py-1.5">{ln.Status || "—"}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-600">{ln["Invoice Number"] || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{ln.qty}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{ln.qty_counted || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(ln.price)}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold">{fmtMoney(ln.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Invoices received */}
            <section className="bg-white border border-slate-200 rounded-sm" data-testid="po-detail-invoices-section">
              <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
                <Receipt size={14} weight="bold" className="text-indigo-700" />
                <span className="text-sm font-bold text-slate-800">Invoices received</span>
                <span className="text-[11px] text-slate-400 font-mono">({invoices.length})</span>
                <div className="flex-1" />
                {invoices.length > 0 && (
                  <span className="text-[11px] text-slate-500 font-mono"
                          data-testid="po-detail-invoices-total">
                    Σ {fmtMoney(invoicesTotal)}
                  </span>
                )}
              </div>
              {invoices.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-4"
                      data-testid="po-detail-invoices-empty">
                  No vendor shipments have been received against this PO yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="po-detail-invoices-table">
                    <thead className="bg-slate-50 text-left text-slate-600">
                      <tr>
                        <th className="px-2 py-1.5 font-semibold">Invoice #</th>
                        <th className="px-2 py-1.5 font-semibold">Date</th>
                        <th className="px-2 py-1.5 font-semibold text-right">Total</th>
                        <th className="px-2 py-1.5 font-semibold">Attachment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id}
                             data-testid={`po-detail-invoice-row-${inv.id}`}
                             onClick={() => setSelectedInvoice(inv.id)}
                             className="border-t border-slate-100 cursor-pointer hover:bg-indigo-50/50">
                          <td className="px-2 py-1.5 font-mono font-bold text-indigo-700">{inv.invoice_number || `#${inv.id}`}</td>
                          <td className="px-2 py-1.5">{inv.invoice_date || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmtMoney(inv.total, inv.currency)}</td>
                          <td className="px-2 py-1.5 text-slate-500">{inv.file_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {selectedInvoice && (
        <VendorShipmentDetailModal
          shipmentId={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  );
}


function KPI({ label, value, mono, highlight }) {
  return (
    <div className={"border border-slate-200 rounded-sm px-2 py-1.5 "
                     + (highlight ? "bg-indigo-50" : "bg-white")}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={"text-sm " + (mono ? "font-mono " : "font-semibold ")
                       + (highlight ? "text-indigo-700 font-bold" : "text-slate-900")}>
        {value}
      </div>
    </div>
  );
}


function FlowChip({ label, value, accent }) {
  const colours = {
    slate:   "bg-slate-50 border-slate-200 text-slate-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    indigo:  "bg-indigo-50 border-indigo-200 text-indigo-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  const n = Number(value || 0);
  return (
    <div className={"rounded-sm border px-3 py-2 " + (colours[accent] || colours.slate)}>
      <div className="text-[10px] uppercase tracking-wider font-bold">{label}</div>
      <div className="text-lg font-bold font-mono">{n.toLocaleString()}</div>
    </div>
  );
}
